// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionWrapper, { SectionWrapperStyles } from "@pace/App/Components/Common/SectionWrapper";
import XngCarousel, { SwiperStyle } from "@pace/App/Components/XngCarousel";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { getSettingsCollection } from "@pace/App/Store/Selectors/SettingsSelectors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const categories: MappedCategory[] = [];
    const categoryIds = ownProps.fields.categories.map(category => {
        return category.fields.categoryItem.type === "Category" ? category.fields.categoryItem.value : null;
    });
    if (categoryIds) {
        categoryIds.forEach(id => {
            const categoryDetails = getCategoryState(state, id!).value;
            categories.push({
                name: categoryDetails?.name,
                path: categoryDetails?.path,
                largeImagePath: categoryDetails?.largeImagePath,
                smallImagePath: categoryDetails?.smallImagePath,
                altText: categoryDetails?.imageAltText,
                id: id!,
            });
        });
    }

    if (!categories) {
        return;
    }

    return {
        categories,
        categoryPage: getPageLinkByPageType(state, "CategoryListPage"),
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        categoryNotFoundLargeImagePath: getSettingsCollection(state).customSettings.categoryNotFoundLargeImagePath,
    };
};

const mapDispatchToProps = {
    loadCategory,
};

interface PopularCategoryFields {
    fields: {
        categoryItem: LinkFieldValue;
    };
}

const enum fields {
    sectionTitle = "sectionTitle",
    categories = "categories",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.sectionTitle]: string;
        [fields.categories]: PopularCategoryFields[];
    };
}

export interface MappedCategory {
    name?: string;
    path?: string;
    largeImagePath?: string;
    smallImagePath?: string;
    altText?: string;
    id?: string;
}
interface PopularCategoriesStyles {
    categoryCardsContainer?: InjectableCss;
    categoryCardClickableWrapper?: ClickablePresentationProps;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    categoryTitleLink?: TypographyProps;
    categoryCarousel?: SwiperStyle;
    sectionWrapperStyles?: SectionWrapperStyles;
}
export const popularCategoriesStyles: PopularCategoriesStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const PopularCategories: React.FC<Props> = ({
    fields,
    categoryPage,
    categories,
    isTabletScreen,
    categoryNotFoundLargeImagePath,
    loadCategory,
}) => {
    const [categoriesState, setCategoriesState] = React.useState(categories);
    
    React.useEffect(() => {
        // Check if there are categories to load
        const categoriesToLoad = categoriesState.filter(c => !c.name && !c.path).map(c => c.id);
        if (categoriesToLoad.length > 0) {
            categoriesToLoad.forEach(id => {
                if(id) {
                    loadCategory({
                        id,
                        onSuccess: (category) => {
                            setCategoriesState(prevState =>
                                prevState.map(c => (c.id === category.id ? category : c))
                            );
                        },
                    });
                }
            });
        }
    }, [categoriesState]);

    if (!fields.categories || fields.categories.length === 0) {
        return null;
    }

    const styles = popularCategoriesStyles;
    const styledProps = { numberOfCards: fields.categories.length };

    const cards = categoriesState.map((category, index) => (
        <Clickable
            href={category.path}
            {...styles.categoryCardClickableWrapper}
            {...styledProps}
            key={index.toString()}
        >
            {/* <StyledWrapper > */}
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage
                    src={category.largeImagePath || categoryNotFoundLargeImagePath}
                    {...styles.image}
                    altText={`${category.altText} category image`}
                />
            </StyledWrapper>
            <Typography {...styles.categoryTitleLink} aria-label={`${category.name}Link `}>
                {category.name}
            </Typography>
            {/* </StyledWrapper> */}
        </Clickable>
    ));
    return (
        <SectionWrapper
            title={fields.sectionTitle}
            extendedStyles={styles.sectionWrapperStyles}
            link={categoryPage?.url}
            linkText={translate("View All Categories")}
            data-test-selector="pace-PopularcategoriesWidget"
        >
            {isTabletScreen ? (
                <XngCarousel
                    scrollbar={{ draggable: true, el: ".swiper-scrollbar" }}
                    navigation={false}
                    extendedStyles={styles.categoryCarousel}
                >
                    {cards}
                </XngCarousel>
            ) : (
                <StyledWrapper {...styles.categoryCardsContainer}>{cards}</StyledWrapper>
            )}
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PopularCategories),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.sectionTitle,
                displayName: "Section Title",
                editorTemplate: "TextField",
                defaultValue: "Popular Categories",
                fieldType: "General",
            },
            {
                name: fields.categories,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        categoryItem: { type, value },
                    } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Category";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "categoryItem",
                        displayName: "Category",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Category", value: "" },
                        allowCategories: (item: HasFields) => true,
                        allowUrls: (item: HasFields) => false,

                        validate: (value: any | null, item: HasFields) => {
                            if (value.type === "Page") {
                                return `You Can only Choose Categories`;
                            }
                            return null;
                        },
                    },
                ],
            },
        ],
    },
};
export default widgetModule;
